/**
 * Admin Proscriptive Workflow Page
 * 
 * Path: /admin/proscriptive/:cycleId
 * Tabs: Cycle Sessions, Audit Log, Store Control
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useRoute } from 'wouter';
import { apiFetch } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Loader2,
  Users,
  FileText,
  Store,
  Lock,
  RefreshCw,
  Calendar,
  Clock,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';

interface CycleSession {
  id: number;
  operatorId: number;
  operatorName: string;
  operatorEmail: string;
  cycleId: number;
  startedAt: string;
  lastActiveAt: string;
  completedAt: string | null;
}

interface AuditEvent {
  id: number;
  cycleId: number | null;
  cycleName: string | null;
  storeId: number;
  storeName: string;
  operatorId: number;
  operatorName: string;
  operatorEmail: string;
  eventType: string;
  previousStatus: string | null;
  newStatus: string;
  metadata: any;
  createdAt: string;
}

interface StoreVisit {
  id: number;
  storeId: number;
  storeName: string;
  operatorId: number;
  operatorName: string;
  status: string;
}

export default function AdminProscriptivePage() {
  const [match, params] = useRoute('/admin/proscriptive/:cycleId');
  const cycleId = params?.cycleId ? parseInt(params.cycleId) : undefined;
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [showCloseCycleDialog, setShowCloseCycleDialog] = useState(false);
  const [showReopenStoreDialog, setShowReopenStoreDialog] = useState(false);
  const [selectedStoreVisitId, setSelectedStoreVisitId] = useState<number | null>(null);
  const [reopenReason, setReopenReason] = useState('');
  const [auditPage, setAuditPage] = useState(0);
  const [auditLimit] = useState(50);

  // Fetch cycle sessions
  const { data: sessionsData, isLoading: sessionsLoading } = useQuery<{
    cycleId: number;
    cycleName: string;
    sessions: CycleSession[];
  }>({
    queryKey: ['admin-proscriptive-sessions', cycleId],
    queryFn: async () => {
      if (!cycleId) throw new Error('Cycle ID required');
      const res = await apiFetch(`/api/admin/proscriptive/cycle/${cycleId}/sessions`);
      if (!res.ok) throw new Error(`Failed to fetch sessions: ${res.status}`);
      return res.json();
    },
    enabled: !!cycleId,
  });

  // Fetch audit log
  const { data: auditData, isLoading: auditLoading } = useQuery<{
    cycleId: number;
    cycleName: string;
    events: AuditEvent[];
    pagination: {
      total: number;
      limit: number;
      offset: number;
      hasMore: boolean;
    };
  }>({
    queryKey: ['admin-proscriptive-audit', cycleId, auditPage],
    queryFn: async () => {
      if (!cycleId) throw new Error('Cycle ID required');
      const res = await apiFetch(
        `/api/admin/proscriptive/cycle/${cycleId}/audit?limit=${auditLimit}&offset=${auditPage * auditLimit}`
      );
      if (!res.ok) throw new Error(`Failed to fetch audit: ${res.status}`);
      return res.json();
    },
    enabled: !!cycleId,
  });

  // Fetch store visits for reopen dropdown
  const { data: storeVisitsData } = useQuery<StoreVisit[]>({
    queryKey: ['admin-proscriptive-store-visits', cycleId],
    queryFn: async () => {
      if (!cycleId) throw new Error('Cycle ID required');
      const res = await apiFetch(`/api/admin/proscriptive/cycle/${cycleId}/store-visits`);
      if (!res.ok) throw new Error(`Failed to fetch store visits: ${res.status}`);
      return res.json();
    },
    enabled: !!cycleId,
  });

  // Close cycle mutation
  const closeCycleMutation = useMutation({
    mutationFn: async () => {
      if (!cycleId) throw new Error('Cycle ID required');
      const res = await apiFetch(`/api/admin/proscriptive/cycle/${cycleId}/close`, {
        method: 'POST',
      });
      if (!res.ok) {
        const error = await res.json().catch(() => ({ message: 'Failed to close cycle' }));
        throw new Error(error.message || error.error || 'Failed to close cycle');
      }
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: 'Cycle closed',
        description: 'Cycle has been locked and all tasks marked as locked',
      });
      setShowCloseCycleDialog(false);
      queryClient.invalidateQueries({ queryKey: ['admin-proscriptive'] });
      queryClient.invalidateQueries({ queryKey: ['cycles'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to close cycle',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Reopen store mutation
  const reopenStoreMutation = useMutation({
    mutationFn: async (storeVisitId: number) => {
      const res = await apiFetch(`/api/admin/proscriptive/store/${storeVisitId}/reopen`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: reopenReason }),
      });
      if (!res.ok) {
        const error = await res.json().catch(() => ({ message: 'Failed to reopen store' }));
        throw new Error(error.message || error.error || 'Failed to reopen store');
      }
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: 'Store reopened',
        description: 'Store has been reopened for corrections',
      });
      setShowReopenStoreDialog(false);
      setReopenReason('');
      setSelectedStoreVisitId(null);
      queryClient.invalidateQueries({ queryKey: ['admin-proscriptive'] });
      queryClient.invalidateQueries({ queryKey: ['store-visits'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to reopen store',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  if (!cycleId) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-red-600">Invalid cycle ID</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Proscriptive Workflow</h1>
          <p className="text-muted-foreground">
            Cycle: {sessionsData?.cycleName || `Cycle ${cycleId}`}
          </p>
        </div>
        <Button
          onClick={() => queryClient.invalidateQueries({ queryKey: ['admin-proscriptive'] })}
          variant="outline"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <Tabs defaultValue="sessions" className="w-full">
        <TabsList>
          <TabsTrigger value="sessions">
            <Users className="h-4 w-4 mr-2" />
            Cycle Sessions
          </TabsTrigger>
          <TabsTrigger value="audit">
            <FileText className="h-4 w-4 mr-2" />
            Audit Log
          </TabsTrigger>
          <TabsTrigger value="store-control">
            <Store className="h-4 w-4 mr-2" />
            Store Control
          </TabsTrigger>
        </TabsList>

        {/* Cycle Sessions Tab */}
        <TabsContent value="sessions">
          <Card>
            <CardHeader>
              <CardTitle>Cycle Sessions</CardTitle>
            </CardHeader>
            <CardContent>
              {sessionsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : sessionsData?.sessions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No sessions found for this cycle</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Operator</TableHead>
                      <TableHead>Started At</TableHead>
                      <TableHead>Last Active</TableHead>
                      <TableHead>Completed At</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sessionsData?.sessions.map((session) => (
                      <TableRow key={session.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{session.operatorName}</div>
                            <div className="text-sm text-muted-foreground">
                              {session.operatorEmail}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {new Date(session.startedAt).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          {new Date(session.lastActiveAt).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          {session.completedAt
                            ? new Date(session.completedAt).toLocaleString()
                            : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit Log Tab */}
        <TabsContent value="audit">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Audit Log</CardTitle>
                <Badge variant="secondary">
                  {auditData?.pagination.total || 0} events
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              {auditLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : auditData?.events.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No audit events found</p>
                </div>
              ) : (
                <>
                  <div className="space-y-4">
                    {auditData?.events.map((event) => (
                      <div
                        key={event.id}
                        className="border rounded-lg p-4 hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge variant="outline">{event.eventType}</Badge>
                              {event.previousStatus && (
                                <>
                                  <span className="text-muted-foreground">→</span>
                                  <Badge>{event.newStatus}</Badge>
                                </>
                              )}
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div>
                                <span className="text-muted-foreground">Store:</span>{' '}
                                {event.storeName}
                              </div>
                              <div>
                                <span className="text-muted-foreground">Operator:</span>{' '}
                                {event.operatorName}
                              </div>
                              <div>
                                <span className="text-muted-foreground">Time:</span>{' '}
                                {new Date(event.createdAt).toLocaleString()}
                              </div>
                              {event.metadata && Object.keys(event.metadata).length > 0 && (
                                <div>
                                  <span className="text-muted-foreground">Metadata:</span>{' '}
                                  <code className="text-xs bg-muted px-1 rounded">
                                    {JSON.stringify(event.metadata)}
                                  </code>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {auditData && auditData.pagination.total > auditLimit && (
                    <div className="flex items-center justify-between mt-4">
                      <Button
                        variant="outline"
                        onClick={() => setAuditPage((p) => Math.max(0, p - 1))}
                        disabled={auditPage === 0}
                      >
                        Previous
                      </Button>
                      <span className="text-sm text-muted-foreground">
                        Page {auditPage + 1} of{' '}
                        {Math.ceil(auditData.pagination.total / auditLimit)}
                      </span>
                      <Button
                        variant="outline"
                        onClick={() => setAuditPage((p) => p + 1)}
                        disabled={!auditData.pagination.hasMore}
                      >
                        Next
                      </Button>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Store Control Tab */}
        <TabsContent value="store-control">
          <Card>
            <CardHeader>
              <CardTitle>Store Control</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Close Cycle */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-semibold flex items-center gap-2">
                      <Lock className="h-5 w-5" />
                      Close Cycle
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Lock the cycle and mark all tasks as locked. All stores must be signed off.
                    </p>
                  </div>
                  <Button
                    onClick={() => setShowCloseCycleDialog(true)}
                    variant="destructive"
                  >
                    Close Cycle
                  </Button>
                </div>
              </div>

              {/* Reopen Store */}
              <div className="border rounded-lg p-4">
                <div className="mb-4">
                  <h3 className="font-semibold flex items-center gap-2 mb-2">
                    <RefreshCw className="h-5 w-5" />
                    Reopen Store
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Reopen a completed or signed-off store for corrections.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Select
                    value={selectedStoreVisitId?.toString() || ''}
                    onValueChange={(v) => setSelectedStoreVisitId(parseInt(v))}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Select a store to reopen" />
                    </SelectTrigger>
                    <SelectContent>
                      {storeVisitsData?.map((visit) => (
                        <SelectItem key={visit.id} value={visit.id.toString()}>
                          {visit.storeName} - {visit.operatorName} ({visit.status})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={() => setShowReopenStoreDialog(true)}
                    disabled={!selectedStoreVisitId}
                  >
                    Reopen Store
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Close Cycle Dialog */}
      <AlertDialog open={showCloseCycleDialog} onOpenChange={setShowCloseCycleDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Close Cycle?</AlertDialogTitle>
            <AlertDialogDescription>
              This will lock the cycle and mark all tasks as locked. All stores must be signed off
              before closing. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => closeCycleMutation.mutate()}
              disabled={closeCycleMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {closeCycleMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Closing...
                </>
              ) : (
                'Close Cycle'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reopen Store Dialog */}
      <Dialog open={showReopenStoreDialog} onOpenChange={setShowReopenStoreDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reopen Store</DialogTitle>
            <DialogDescription>
              Provide a reason for reopening this store. The signature will be cleared and the
              store status will be set to 'reopened'.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Reason</label>
              <Textarea
                value={reopenReason}
                onChange={(e) => setReopenReason(e.target.value)}
                placeholder="Enter reason for reopening..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowReopenStoreDialog(false);
                setReopenReason('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (selectedStoreVisitId && reopenReason.trim()) {
                  reopenStoreMutation.mutate(selectedStoreVisitId);
                }
              }}
              disabled={!reopenReason.trim() || reopenStoreMutation.isPending}
            >
              {reopenStoreMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Reopening...
                </>
              ) : (
                'Reopen Store'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

